import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import {
  fetchBlogPostBySlug,
  formatBlogDate,
  isSanityConfigured,
} from "../lib/sanity";

const portableComponents = {
  block: {
    h2: ({ children }) => <h2 className="blog-post-h2">{children}</h2>,
    h3: ({ children }) => <h3 className="blog-post-h3">{children}</h3>,
    normal: ({ children }) => <p className="blog-post-p">{children}</p>,
    blockquote: ({ children }) => (
      <blockquote className="blog-post-quote">{children}</blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => <ul className="blog-post-ul">{children}</ul>,
    number: ({ children }) => <ol className="blog-post-ol">{children}</ol>,
  },
  marks: {
    link: ({ children, value }) => {
      const href = value?.href || "#";
      const isExternal = href.startsWith("http");
      return (
        <a
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
        >
          {children}
        </a>
      );
    },
    strong: ({ children }) => <strong>{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
  },
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isSanityConfigured()) {
        setLoading(false);
        setError("cms-not-configured");
        return;
      }

      try {
        const data = await fetchBlogPostBySlug(slug);
        if (!cancelled) {
          if (!data) {
            setError("Post not found.");
            setPost(null);
          } else {
            setPost(data);
            setError("");
          }
        }
      } catch (err) {
        console.error("Failed to load blog post:", err);
        if (!cancelled) {
          setError("Failed to load this post. Please try again later.");
          setPost(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="blog-page">
      <nav className="navbar navbar-default navbar-fixed-top">
        <div className="container">
          <div className="navbar-header">
            <Link className="navbar-brand" to="/">
              By The App
            </Link>
          </div>
          <div className="collapse navbar-collapse">
            <ul className="nav navbar-nav navbar-right">
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>
                <Link to="/#contact">Contact</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <main className="blog-post-page">
        <div className="container">
          <div className="blog-post-inner">
            <p className="blog-back">
              <Link to="/blog">← Back to blog</Link>
            </p>

            {loading && <p className="blog-status">Loading…</p>}

            {!loading && error === "cms-not-configured" && (
              <div className="blog-empty">
                <p>Blog CMS is not connected yet.</p>
                <p className="blog-empty-hint">
                  See <code>BLOG_CMS_SETUP.md</code> to connect Sanity.
                </p>
              </div>
            )}

            {!loading && error && error !== "cms-not-configured" && (
              <div className="blog-empty">
                <p>{error}</p>
                <Link to="/blog">View all posts</Link>
              </div>
            )}

            {!loading && post && (
              <article>
                <div className="blog-card-meta">
                  <time dateTime={post.publishedAt}>
                    {formatBlogDate(post.publishedAt)}
                  </time>
                  {post.readMinutes ? (
                    <span>{post.readMinutes} MIN</span>
                  ) : null}
                </div>
                <h1 className="blog-post-title">{post.title}</h1>
                {post.excerpt && (
                  <p className="blog-post-excerpt">{post.excerpt}</p>
                )}
                {post.mainImage?.asset?.url && (
                  <img
                    className="blog-post-image"
                    src={post.mainImage.asset.url}
                    alt={post.mainImage.alt || post.title}
                  />
                )}
                <div className="blog-post-body">
                  {post.body ? (
                    <PortableText
                      value={post.body}
                      components={portableComponents}
                    />
                  ) : null}
                </div>
              </article>
            )}
          </div>
        </div>
      </main>

      <div id="footer">
        <div className="container text-center">
          <p>
            &copy; 2026 ByTheApp. All rights reserved.{" "}
            <Link to="/blog">Blog</Link>
            {" · "}
            <Link to="/privacy">Privacy Policy</Link>
            {" · "}
            <Link to="/terms">Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
